import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext({
  user: null,
  loading: true,
  loginWithJwt: async () => {},
  registerJwt: async () => {},
  loginWithSessionId: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = checkAuth;

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithJwt = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("hq_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const loginWithCode = async (code) => {
    const { data } = await api.post("/auth/code-login", { code });
    localStorage.setItem("hq_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const registerJwt = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    localStorage.setItem("hq_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const loginWithSessionId = async (sessionId) => {
    const { data } = await api.post("/auth/session", { session_id: sessionId });
    localStorage.setItem("hq_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore — still purge locally */
    }
    localStorage.removeItem("hq_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginWithJwt, loginWithCode, registerJwt, loginWithSessionId, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
